import axios from 'axios';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import parseCatch from 'utils/parseCatch';

import {
  ACTION,
  ENUM_PROFILE_GALLERY_TYPE,
  FILE_STATUS,
  ERROR_STATE,
} from './config';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const GALLERIES_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries`;
const getGalleriesUrl = (username) => {
  const url = ApiService.substitute(GALLERIES_URL, { username: encodeURIComponent(username) });

  return url;
};
const IMAGES_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images?{status}`;
const getImagesUrl = ({ username, galleryId }) => {
  let url = ApiService.substitute(IMAGES_URL, {
    username: encodeURIComponent(username),
    galleryId: encodeURIComponent(galleryId),
  });

  const searchParams = new URLSearchParams();
  searchParams.append('status', 'SFW');
  searchParams.append('status', 'NSFW');
  searchParams.append('status', 'REJECTED');
  searchParams.append('status', 'PENDING');

  url = ApiService.substitute(url, { status: searchParams.toString() });

  return url;
};

const GALLERIES_UPDATE_URL_NEW = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}`;
const getGalleriesUpdateUrl = ({ username, galleryId }) => {
  const url = ApiService.substitute(GALLERIES_UPDATE_URL_NEW, {
    username: encodeURIComponent(username),
    galleryId: encodeURIComponent(galleryId),
  });

  return url;
};

const IMAGE_DELETE_URL_NEW = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images/{imageId}`;
const getImageDeleteUrl = ({ username, galleryId, imageId }) => {
  const url = ApiService.substitute(IMAGE_DELETE_URL_NEW, {
    username: encodeURIComponent(username),
    galleryId: encodeURIComponent(galleryId),
    imageId: encodeURIComponent(imageId),
  });

  return url;
};

const IMAGES_UPLOAD_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images`;
const getImagesUploadUrl = ({ username, galleryId }) => {
  const url = ApiService.substitute(IMAGES_UPLOAD_URL, {
    username: encodeURIComponent(username),
    galleryId: encodeURIComponent(galleryId),
  });

  return url;
};


export async function doCreateNewGallery({ username, galleryName, galleryType }) {
  const url = getGalleriesUrl(username);

  let result = null;
  try {
    const config = {
      data: {
        name: galleryName,
        galleryType,
      },
    };
    result = await ApiService.post(url, config);
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function doDeleteGallery({ username, galleryId }) {
  const url = getGalleriesUpdateUrl({ username, galleryId });
  let result = null;
  try {
    result = await ApiService.delete(url);
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function fetchGalleries(username, dispatch) {
  const url = getGalleriesUrl(username);
  const config = {};
  const searchParams = new URLSearchParams();
  searchParams.append('imageStatus', 'SFW');
  searchParams.append('imageStatus', 'NSFW');
  searchParams.append('imageStatus', 'REJECTED');
  searchParams.append('imageStatus', 'PENDING');

  config.params = searchParams;

  let result = null;
  try {
    const response = await ApiService.get(url, config);
    result = response.data;
    const profileGallery = result.find((item) => item.galleryType === ENUM_PROFILE_GALLERY_TYPE.PROFILE);

    // always keep PROFILE gallery type as first item in array
    if (result.length > 0 && profileGallery) {
      const filtered = result.filter((item) => item.galleryType !== ENUM_PROFILE_GALLERY_TYPE.PROFILE);
      result = [profileGallery, ...filtered];
    }

    dispatch({
      type: ACTION.FETCH_GALLERIES,
      payload: result,
    });
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function updateGalleries({ username, galleryId, data }) {
  const url = getGalleriesUpdateUrl({ username, galleryId });

  let result = null;
  try {
    result = await ApiService.put(url, { data });
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function fetchPhotos({ username, galleryId }, dispatch, params) {
  const url = getImagesUrl({ username, galleryId });

  let result = {};
  try {
    const response = await ApiService.get(url, { params });
    dispatch({
      type: ACTION.FETCH_PHOTOS,
      payload: response.data,
    });
  } catch (error) {
    parseCatch(error);
    result = Promise.reject();
  }

  return result;
}

export async function doDeleteImageFromGallery({ username, galleryId, imageId }) {
  const url = getImageDeleteUrl({ username, galleryId, imageId });

  let result = null;
  try {
    result = await ApiService.delete(url);
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function doPostImageToGallery({ username, image, galleryId }, cb, successCb, errorCb) {
  const url = getImagesUploadUrl({ username, galleryId });

  const formData = new FormData();
  formData.append('image', image.file);
  if (image.mainImage) {
    formData.append('mainImage', true);
  }
  const config = {
    headers: { 'content-type': 'multipart/form-data' },
    withCredentials: true,
    onUploadProgress(progressEvent) {
      cb(image, progressEvent);
    },
  };

  let result = null;
  try {
    result = await axios.post(url, formData, config);
    if (result.status === StatusCodes.OK) {
      successCb(image);
    }
  } catch (error) {
    parseCatch(error);
    errorCb(image);
    result = FILE_STATUS.ERROR;
  }

  return result;
}

export async function doAddImageToGallery(data, cb) {
  const { username, galleryId, images } = data;

  const setStatus = (file, obj) =>
    images.map((image) => {
      if (image.id === file.id) {
        return Object.assign(file, obj);
      }

      return image;
    });

  const errors = [
    ERROR_STATE.MAX_SIZE,
    ERROR_STATE.INVALID_FORMAT,
    ERROR_STATE.SIZE_AND_FORMAT,
  ];

  const filesToUpload = [];
  for (const image of images) {
    if (errors.includes(image.status)) {
      continue;
    }

    const promise = doPostImageToGallery(
      { username, image, galleryId },
      (file, progressEvent) => {
        let totalLength = progressEvent.total;
        if (!progressEvent.lengthComputable) {
          totalLength =
              progressEvent.target.getResponseHeader('content-length') ||
              progressEvent.target.getResponseHeader('x-decompressed-content-length');
        }

        let progress = 0;
        if (totalLength !== null) {
          progress = Math.round((progressEvent.loaded * 100) / totalLength);
        }

        cb(setStatus(file, { progress }));
      },
      (file) => cb(setStatus(file, { status: FILE_STATUS.SUCCESS })),
      (file) => cb(setStatus(file, { status: FILE_STATUS.ERROR }))
    );

    filesToUpload.push(promise);
  }

  let result = null;
  try {
    result = await Promise.all(filesToUpload);
  } catch (error) {
    parseCatch(error);
  }

  return result;
}

export async function fetchGalleryProfileImage(username) {
  const url = getGalleriesUrl(username);

  let result = null;
  try {
    const response = await ApiService.get(url);
    result = response.data;

  } catch (error) {
    parseCatch(error);
  }

  return result;
}
